import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useKeycloakAuthz } from "../../../components/keycloak-provider";
import { useConfig } from "../../../lib/config";
import { DASHBOARD_FILTER_CRITERIA } from "../../witadmin/Common/constants";
import { formatNumberWithLetters } from "../components/charts";
import { FilterDashboard } from "../types/types";

// Checks if the filter criteria is money-related (e.g. total amount)
export const isMoneyFilterCriteria = ({ criteria }) =>
  criteria.labelName === DASHBOARD_FILTER_CRITERIA.BY_CHECKIN_DATE ||
  criteria.labelName === DASHBOARD_FILTER_CRITERIA.BY_CHECKOUT_DATE;

interface UseGetDashboardProps {
  criteria: FilterDashboard;
  start: string; // "2025-02-24"
  end: string; // "2025-03-06"
  propertyTicker: string;
}

type ReservationInfo = {
  count: number;
  day: string; // ISO "YYYY-MM-DD"
  averageAmount: number;
  totalAmount: number;
};
interface DashboardDataPoint {
  x: string; // DD/MM Format
  y: number;
  formattedY: string; // Formatted value with letters
  fullDate: string; // Full date DD/MM/YYYY
}

export const useGetDashboard = ({ criteria, start, end, propertyTicker }: UseGetDashboardProps) => {
  const [data, setData] = useState<{
    currentYear: DashboardDataPoint[];
    pastYear: DashboardDataPoint[];
  }>({
    currentYear: [],
    pastYear: [],
  });
  const [loading, setLoading] = useState(true);

  const config = useConfig();
  const keycloak = useKeycloakAuthz();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1) Fetch raw data arrays from backend
        const { data: resp } = await axios.post(
          `${config.dashboardUrl}/${propertyTicker}`,
          { criteria: criteria.value.toUpperCase(), start, end },
          { headers: { Authorization: keycloak.rpt } }
        );

        const moneyMode = isMoneyFilterCriteria({ criteria });

        // Get current year and past year from the start date
        const currentYearValue = dayjs(start).year();
        const pastYearValue = currentYearValue - 1;

        // 2) Map both series to { x: "DD/MM", rawY, fullDate }
        const currentRaw = resp.reservationInfo.currentYear.map((it: ReservationInfo) => {
          return {
            x: dayjs(it.day).format("DD/MM"),
            rawY: moneyMode ? it.totalAmount : it.count,
            fullDate: dayjs(it.day).format("DD/MM/YYYY"),
            originalDate: it.day, // Store the original date
          };
        });
        const pastRaw = resp.reservationInfo.pastYear.map((it: ReservationInfo) => {
          return {
            x: dayjs(it.day).format("DD/MM"),
            rawY: moneyMode ? it.totalAmount : it.count,
            fullDate: dayjs(it.day).format("DD/MM/YYYY"),
            originalDate: it.day, // Store the original date
          };
        });

        // 3) Generate all dates between start and end to ensure all days appear
        const startDate = dayjs(start);
        const endDate = dayjs(end);
        const daysDiff = endDate.diff(startDate, "day");

        // Array to store all dates in DD/MM format
        const allDates: string[] = [];

        // Generate all dates between start and end
        for (let i = 0; i <= daysDiff; i++) {
          const date = startDate.add(i, "day");
          const dateFormatted = date.format("DD/MM");
          allDates.push(dateFormatted);
        }

        // Add Feb 29th if it exists in either current or previous year but not in the generated range
        const has29FebInPastYear = pastRaw.some(item => item.x === "29/02");
        const has29FebInCurrentYear = currentRaw.some(item => item.x === "29/02");
        const has29Feb = has29FebInPastYear || has29FebInCurrentYear;

        if (has29Feb && !allDates.includes("29/02")) {
          allDates.push("29/02");
        }

        // 4) Sort dates chronologically using a fictional year
        const sortedDates = allDates.sort((a, b) => {
          const [da, ma] = a.split("/");
          const [db, mb] = b.split("/");
          const dateA = dayjs(`2000-${ma}-${da}`, "YYYY-MM-DD");
          const dateB = dayjs(`2000-${mb}-${db}`, "YYYY-MM-DD");
          return dateA.valueOf() - dateB.valueOf();
        });

        // 5) Fill each series, setting y=0 where no data exists
        const currentYear = sortedDates.map(key => {
          const [day, month] = key.split("/");
          const found = currentRaw.find(r => r.x === key);
          const y = found ? found.rawY : 0;

          // Check if this date is valid in the current year
          const isValidDate = dayjs(`${currentYearValue}-${month}-${day}`).isValid();

          let fullDate = found
            ? found.fullDate
            : isValidDate
            ? `${day}/${month}/${currentYearValue}`
            : `${day}/${month}/${currentYearValue} (No existe)`;

          return {
            x: key,
            y,
            formattedY: formatNumberWithLetters(y),
            fullDate: fullDate,
            isValid: isValidDate,
          };
        });

        const pastYear = sortedDates.map(key => {
          const [day, month] = key.split("/");
          const found = pastRaw.find(r => r.x === key);
          const y = found ? found.rawY : 0;

          // Check if this date is valid in the past year
          const isValidDate = dayjs(`${pastYearValue}-${month}-${day}`).isValid();

          let fullDate = found
            ? found.fullDate
            : isValidDate
            ? `${day}/${month}/${pastYearValue}`
            : `${day}/${month}/${pastYearValue} (No existe)`;

          return {
            x: key,
            y,
            formattedY: formatNumberWithLetters(y),
            fullDate: fullDate,
            isValid: isValidDate,
          };
        });

        setData({ currentYear, pastYear });
      } catch (err) {
        console.error("Error in dashboard hook", err);
      } finally {
        setLoading(false);
      }
    };

    if (start && end && criteria?.value) {
      setLoading(true);
      fetchData();
    }
  }, [config.dashboardUrl, criteria, start, end, propertyTicker, keycloak]);

  return { data, loading };
};
